/**
 * Accessible tooltip (UX-SPEC §5 "Onboarding & tooltips").
 *
 * - The trigger's `aria-describedby` points at a dedicated, always-present
 *   sr-only text ("Shuffle your rack. Shortcut: Shift S"), so screen readers
 *   announce the description (including a disabled reason) on focus. It is
 *   `hidden`: a description is still computed from a hidden node it names,
 *   but browse mode no longer reads every tip at the end of <body>, and it is
 *   not landmark-less page content (axe "region"). The
 *   visual bubble (role="tooltip", key caps) is aria-hidden: describing from
 *   it read the shortcut twice (sr text + key caps) with a stray " .".
 * - No description when the trigger's own aria-label already says it (theme
 *   radios "Light theme", "Game menu" for "Menu") and there is no shortcut.
 * - Shows after 400ms of mouse/pen hover, immediately on keyboard focus
 *   (`<html data-keyboard>` from lib/inputModality: the last input was a key).
 *   Never on touch, not even on programmatic focus after a reload: a
 *   long-press would fight with dragging tiles.
 * - Rendered in a portal with fixed positioning, flipped below the trigger
 *   when there is no room above and clamped to the viewport.
 */
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import './Tooltip.css';

const HOVER_DELAY = 400;
const MARGIN = 8;

/** Plain text of a tooltip's content, when it is text (null for rich content). */
function textOf(content: ReactNode): string | null {
  if (typeof content === 'string') return content;
  if (typeof content === 'number') return String(content);
  return null;
}

/** "Shuffle your rack" + "Shift S" → "Shuffle your rack. Shortcut: Shift S" (no ".." after a full stop). */
export function describeTip(content: string, shortcut?: string): string {
  if (!shortcut) return content;
  const sep = /[.!?…]$/.test(content.trim()) ? ' ' : '. ';
  return `${content.trim()}${sep}Shortcut: ${shortcut}`;
}

interface TooltipProps {
  content: ReactNode;
  /** Keyboard shortcut shown as a key cap, e.g. "Shift S". */
  shortcut?: string;
  placement?: 'top' | 'bottom';
  /** The trigger: a single element that accepts `aria-describedby`. */
  children: ReactElement<{ 'aria-describedby'?: string; 'aria-label'?: string }>;
  disabled?: boolean;
}

export function Tooltip({ content, shortcut, placement = 'top', children, disabled = false }: TooltipProps) {
  const id = useId();
  const wrapRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number; side: 'top' | 'bottom' } | null>(null);

  const clear = () => window.clearTimeout(timer.current);
  const hide = useCallback(() => {
    window.clearTimeout(timer.current);
    setOpen(false);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // Close on scroll / resize / Escape while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, hide]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const anchor = wrapRef.current?.firstElementChild as HTMLElement | null;
    const bubble = bubbleRef.current;
    if (!anchor || !bubble) return;
    const a = anchor.getBoundingClientRect();
    const b = bubble.getBoundingClientRect();
    const vw = window.innerWidth;
    let side = placement;
    if (side === 'top' && a.top - b.height - MARGIN < 0) side = 'bottom';
    else if (side === 'bottom' && a.bottom + b.height + MARGIN > window.innerHeight) side = 'top';
    const top = side === 'top' ? a.top - b.height - MARGIN : a.bottom + MARGIN;
    const left = Math.min(Math.max(MARGIN, a.left + a.width / 2 - b.width / 2), vw - b.width - MARGIN);
    setPos({ left, top, side });
  }, [open, placement, content]);

  if (!isValidElement(children)) return children;
  const text = textOf(content);
  const label = children.props['aria-label'];
  // A description that only repeats the accessible name is noise.
  const redundant = !shortcut && text != null && !!label && label.toLowerCase().includes(text.trim().toLowerCase());
  const describe = !disabled && !redundant;
  const trigger = cloneElement(children, {
    'aria-describedby': clsx(children.props['aria-describedby'], describe && id) || undefined,
  });

  const onPointerEnter = (e: PointerEvent) => {
    if (disabled || e.pointerType === 'touch') return;
    clear();
    timer.current = window.setTimeout(() => setOpen(true), HOVER_DELAY);
  };
  const onFocus = () => {
    // Keyboard focus only, judged by the last input event (<html data-keyboard>,
    // lib/inputModality), not :focus-visible: Chromium and WebKit match that on
    // programmatic focus in a reloaded tab, which opened a bubble on a phone.
    if (disabled || !document.documentElement.hasAttribute('data-keyboard')) return;
    clear();
    setOpen(true);
  };

  return (
    <>
      <span
        ref={wrapRef}
        className="tip-anchor"
        onPointerEnter={onPointerEnter}
        onPointerLeave={hide}
        onPointerDown={hide}
        onFocus={onFocus}
        onBlur={hide}
      >
        {trigger}
      </span>
      {!disabled &&
        createPortal(
          <>
            {describe && (
              <span id={id} className="sr-only" hidden>
                {text != null ? describeTip(text, shortcut) : content}
                {text == null && shortcut ? `. Shortcut: ${shortcut}` : null}
              </span>
            )}
            <div
              ref={bubbleRef}
              role="tooltip"
              aria-hidden="true"
              className={clsx('tooltip', pos && `tooltip--${pos.side}`, open && pos && 'is-open')}
              hidden={!open}
              style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}
            >
              <span className="tooltip__text">{content}</span>
              {shortcut && (
                <span className="tooltip__keys">
                  {shortcut.split(' ').map((k) => (
                    <kbd key={k} className="kbd kbd--dark">
                      {k}
                    </kbd>
                  ))}
                </span>
              )}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
